import React from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import HistoryIcon from "@mui/icons-material/History";
import { IconButton, Chip, Box, Divider } from "@mui/material";
import { DateTime } from "luxon";
import AssignmentBasedRulesList from "./AssignmentBasedRulesList";

const RuleCard = ({ rule, redirectPage }: any) => {
  const handleClick = () => {
    const ruleRoute = `/rule/${rule.url}`;
    redirectPage(ruleRoute);
  };

  const handleHistoryClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    // Navigate to history page with rule group filter if available
    redirectPage("/history");
  };

  const ruleCount = rule?.ruleAssignments?.length || 0;
  const activeRuleCount = rule?.ruleAssignments?.filter((r: any) => 
    r.conditions?.some((c: any) => c.isActive)
  ).length || 0;

  return (
    <Card
      onClick={handleClick}
      sx={{ boxShadow: 3, width: 1, padding: "10px", cursor: "pointer", "&:hover": { boxShadow: 6 } }}
    >
      <CardContent>
        <Grid container>
          <Grid item xs={12} container justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
            <Grid item xs={8}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                <Typography
                  variant="h6"
                  component="div"
                  sx={{ textDecoration: "underline", cursor: "pointer" }}
                >
                  {rule?.name}
                </Typography>
                <IconButton size="small">
                  <OpenInNewIcon fontSize="small" />
                </IconButton>
                {rule?.isDefault && (
                  <Chip 
                    label="Default" 
                    size="small" 
                    color="primary" 
                    sx={{ ml: 1 }}
                  />
                )}
                {rule?.isActive && (
                  <Chip 
                    label="Active" 
                    size="small" 
                    color="success" 
                    sx={{ ml: 1 }}
                  />
                )}
                {rule?.isInactive && (
                  <Chip 
                    label="Inactive" 
                    size="small" 
                    color="warning" 
                    sx={{ ml: 1 }}
                  />
                )}
              </Box>
              <Typography color="text.secondary" variant="subtitle2">
                {rule?.description}
              </Typography>
              <Typography color="text.secondary" variant="caption" sx={{ display: "block", mt: 1 }}>
                {rule?.startDate &&
                  rule?.endDate &&
                  `Active between ${rule?.startDate?.toLocaleString()} and ${rule?.endDate?.toLocaleString()}`}
                {rule?.startDate &&
                  !rule?.endDate &&
                  `Active after ${rule?.startDate?.toLocaleString()}`}
                {!rule?.startDate &&
                  rule?.endDate &&
                  `Active until ${rule?.endDate?.toLocaleString()}`}
              </Typography>
            </Grid>
            <Grid item xs={4} container direction="column" alignItems="flex-end" spacing={1}>
              <Grid item>
                <Typography
                  color="text.secondary"
                  sx={{ lineHeight: 1.7 }}
                  variant="body2"
                  align="right"
                >
                  Last updated by <br /> {rule?.lastUpdatedUserName} <br />
                  {rule?.lastUpdatedDate?.toLocaleString(DateTime.DATETIME_SHORT)}
                </Typography>
              </Grid>
              <Grid item>
                <IconButton 
                  size="small" 
                  onClick={handleHistoryClick}
                  sx={{ mt: 1 }}
                  title="View execution history"
                >
                  <HistoryIcon />
                </IconButton>
              </Grid>
            </Grid>
          </Grid>

          {/* Rule Statistics */}
          {!rule?.isDefault && ruleCount > 0 && (
            <Grid item xs={12} sx={{ mb: 2 }}>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                <Chip 
                  label={`${ruleCount} Rule${ruleCount !== 1 ? 's' : ''}`} 
                  size="small" 
                  variant="outlined"
                  color="primary"
                />
                <Chip 
                  label={`${activeRuleCount} Active`} 
                  size="small" 
                  variant="outlined"
                  color="success"
                />
              </Box>
            </Grid>
          )}

          {rule?.isDefault ? null : (
            <Grid item xs={12} mt={2} ml={3}>
              <AssignmentBasedRulesList rule={rule} />
            </Grid>
          )}
        </Grid>
      </CardContent>
    </Card>
  );
};

export default RuleCard;
